import { useEffect, useState } from "react";
import { api, payload } from "./lib/api";

// Pagamenti online (Stripe) per tutte le case del gruppo: la chiave si incolla qui una volta,
// il gestionale la cifra, crea da solo il webhook e non la mostra mai più.
type Payments = {
  configured: boolean; enabled: boolean; mode: "test" | "live" | null; accountName: string; updatedAt: string | null; webhookReady?: boolean;
  cardOnly?: { ready: boolean; error: string; checkedAt: string | null };
};

export default function RentalPayments({ siteId, canEdit }: { siteId: string; canEdit: boolean }) {
  const [payments, setPayments] = useState<Payments | null>(null);
  const [secretKey, setSecretKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api<{ payments: Payments }>(`/api/sites/${siteId}/rental-payments`).then((result) => setPayments(result.payments)).catch(() => setPayments(null));
  }, [siteId]);

  async function save(enabled: boolean, withKey: boolean) {
    setBusy(true); setMessage(""); setError("");
    try {
      const result = await api<{ payments: Payments }>(`/api/sites/${siteId}/rental-payments`, payload("PUT", { secretKey: withKey ? secretKey.replace(/\s+/g, "") : "", enabled }));
      setPayments(result.payments);
      setSecretKey("");
      setMessage(withKey ? (result.payments.mode === "live" && !result.payments.enabled
        ? "Chiave vera verificata con Stripe e collegamento creato. Il pagamento sul sito è ancora spento: accendilo quando prezzi e condizioni sono pronti."
        : "Chiave verificata con Stripe e collegamento creato.") : enabled ? "Pagamento online attivo sul sito." : "Pagamento online spento: sul sito resta solo la richiesta senza addebito.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Salvataggio non riuscito");
    } finally { setBusy(false); }
  }

  return <section className="rental-payments">
    <h3>Pagamenti online (Stripe)</h3>
    <p className="escape-resource-note">Un solo account Stripe per Cortirola e Nuovo Fiore. I soldi vanno direttamente sul conto collegato a Stripe; il gestionale non vede i dati delle carte.</p>
    {payments?.configured ? <p className="rental-payments-state">
      <strong>{payments.accountName || "Account Stripe"}</strong> · {payments.mode === "live" ? "incassi veri" : "modalità prova (nessun incasso, il calendario non cambia)"}
      {" · "}{payments.enabled ? "attivo sul sito" : "spento sul sito"}
    </p> : <p className="rental-payments-state">Nessun account collegato: sul sito non si può prenotare online.</p>}
    {payments?.configured && (payments.cardOnly?.ready
      ? <p className="rental-payments-state">✓ Metodi di pagamento: solo carta (anche Apple Pay e Google Pay, che pagano con una carta). Niente bonifici, Klarna o PayPal.</p>
      : <p className="rental-rate-warning">Non sono riuscito a impostare «solo carta» in Stripe{payments.cardOnly?.error ? `: ${payments.cardOnly.error}` : ""}. I pagamenti funzionano lo stesso, ma Stripe può proporre anche altri metodi. Il gestionale riprova da solo al prossimo pagamento; se l'errore parla di permessi, la chiave deve poter gestire le «Payment method configurations».</p>)}
    {canEdit && <>
      <label>Chiave segreta di Stripe (sk_test_… per provare, sk_live_… per incassare)
        {/* Campo in chiaro e non «password»: così Chrome non ci inserisce da solo una password salvata e si vede cosa si è incollato. */}
        <input type="text" name="stripe-secret-key" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} data-1p-ignore data-lpignore="true"
          value={secretKey} onChange={(event) => setSecretKey(event.target.value)} placeholder={payments?.configured ? "Lascia vuoto per tenere quella salvata" : "sk_live_… oppure sk_test_…"} />
      </label>
      {secretKey && <p className="escape-resource-note">Lunghezza: {secretKey.replace(/\s+/g, "").length} caratteri{/[.…*•]/.test(secretKey) ? " · contiene puntini o asterischi: è la versione nascosta, non la chiave intera" : ""}</p>}
      <p className="escape-resource-note">La chiave si copia da Stripe → Sviluppatori → Chiavi API e si incolla solo qui: mai in chat, email o file. Dopo il salvataggio non è più visibile.</p>
      <div className="rental-payments-actions">
        <button type="button" className="primary" disabled={busy || !secretKey.trim()} onClick={() => void save(payments?.enabled ?? true, true)}>{busy ? "Verifica con Stripe…" : "Verifica e salva la chiave"}</button>
        {payments?.configured && <button type="button" disabled={busy} onClick={() => void save(!payments.enabled, false)}>{payments.enabled ? "Spegni il pagamento sul sito" : "Accendi il pagamento sul sito"}</button>}
      </div>
    </>}
    {message && <p className="success-message">{message}</p>}
    {error && <p className="error-message">{error}</p>}
  </section>;
}
